import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, ContentItem } from '../lib/api';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  EyeIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

export default function ContentReviewPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const [revisionFeedback, setRevisionFeedback] = useState('');

  useEffect(() => {
    loadContent();
  }, [slug]);

  const loadContent = async () => {
    if (!slug) return;

    try {
      const response = await api.getPhaseReview(slug);
      setContentItems(response.content_items);
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewItem = async (item: ContentItem) => {
    try {
      const fullItem = await api.getContentItem(item.id);
      setSelectedItem(fullItem);
    } catch (error) {
      console.error('Failed to load full content:', error);
    }
  };

  const handleApprove = async () => {
    if (!slug) return;

    setIsApproving(true);
    try {
      await api.approvePhase(slug);
      navigate('/');
    } catch (error) {
      console.error('Failed to approve:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleRequestRevision = async () => {
    if (!slug || !revisionFeedback.trim()) return;

    try {
      await api.requestRevision(slug, revisionFeedback);
      setShowRevisionForm(false);
      setRevisionFeedback('');
      navigate('/');
    } catch (error) {
      console.error('Failed to request revision:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-copper-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <Link
        to={`/phases/${slug}`}
        className="inline-flex items-center text-charcoal-300 hover:text-copper-600 transition-colors text-sm mb-6"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
        Back to phase
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Content List */}
        <div className="lg:col-span-1 space-y-4">
          <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm">
            {/* Decorative element */}
            <div className="absolute inset-0 pointer-events-none">
              <svg className="absolute right-3 top-3 w-16 h-16 opacity-[0.03]" viewBox="0 0 64 64">
                <circle cx="48" cy="16" r="28" fill="none" stroke="#2C3E3D" strokeWidth="1" />
                <circle cx="48" cy="16" r="16" fill="none" stroke="#2C3E3D" strokeWidth="0.7" />
              </svg>
            </div>

            <div className="h-1 bg-gradient-to-r from-copper-400 via-sage-400 to-teal-400" />

            <div className="relative p-6">
              <div className="flex items-center gap-2 mb-4">
                <SparklesIcon className="h-5 w-5 text-copper-400" />
                <h2 className="text-xl font-bold text-charcoal-500">
                  Generated Content
                </h2>
              </div>

              {contentItems.length === 0 ? (
                <p className="text-charcoal-300 text-sm">No content available yet</p>
              ) : (
                <div className="space-y-2">
                  {contentItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleViewItem(item)}
                      className={`w-full text-left p-3 rounded-xl border transition-all ${
                        selectedItem?.id === item.id
                          ? 'bg-copper-50 border-copper-300 shadow-sm'
                          : 'hover:bg-cream-50 border-charcoal-100 hover:border-charcoal-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${
                            selectedItem?.id === item.id ? 'text-copper-700' : 'text-charcoal-500'
                          }`}>
                            {item.title}
                          </p>
                          <p className="text-xs text-charcoal-300 mt-1">
                            {item.content_type}
                          </p>
                        </div>
                        <EyeIcon className={`h-5 w-5 ml-2 flex-shrink-0 ${
                          selectedItem?.id === item.id ? 'text-copper-400' : 'text-charcoal-200'
                        }`} />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm p-5 space-y-3">
            <button
              onClick={handleApprove}
              disabled={isApproving || contentItems.length === 0}
              className="btn btn-primary w-full flex items-center justify-center disabled:opacity-50 shadow-sm shadow-copper-500/20"
            >
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              {isApproving ? 'Approving...' : 'Approve & Continue'}
            </button>

            <button
              onClick={() => setShowRevisionForm(true)}
              disabled={contentItems.length === 0}
              className="btn btn-outline w-full flex items-center justify-center disabled:opacity-50"
            >
              <PencilSquareIcon className="h-5 w-5 mr-2" />
              Request Revisions
            </button>
          </div>
        </div>

        {/* Content Preview */}
        <div className="lg:col-span-2">
          {showRevisionForm ? (
            <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm p-6">
              <h2 className="text-xl font-bold text-charcoal-500 mb-4">
                Request Revisions
              </h2>

              <p className="text-charcoal-300 mb-4">
                Please provide specific feedback about what you'd like changed:
              </p>

              <textarea
                value={revisionFeedback}
                onChange={(e) => setRevisionFeedback(e.target.value)}
                rows={6}
                className="input mb-4"
                placeholder="Example: Please make the homepage headline more action-oriented and emphasize our 24/7 emergency service..."
              />

              <div className="flex gap-3">
                <button
                  onClick={handleRequestRevision}
                  disabled={!revisionFeedback.trim()}
                  className="btn btn-primary disabled:opacity-50"
                >
                  Submit Revision Request
                </button>
                <button
                  onClick={() => setShowRevisionForm(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : selectedItem ? (
            <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm">
              {/* Decorative elements */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-16 -right-16 w-48 h-48 bg-[radial-gradient(circle,rgba(196,117,76,0.04),transparent_70%)]" />
                <svg className="absolute right-4 top-4 w-20 h-20 opacity-[0.03]" viewBox="0 0 80 80">
                  <circle cx="60" cy="20" r="35" fill="none" stroke="#2C3E3D" strokeWidth="1" />
                  <circle cx="60" cy="20" r="20" fill="none" stroke="#2C3E3D" strokeWidth="0.7" />
                </svg>
              </div>

              <div className="relative p-6">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-charcoal-500">{selectedItem.title}</h2>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-charcoal-50 text-charcoal-500">{selectedItem.content_type}</span>
                    {selectedItem.compliance_status === 'pass' && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-sage-50 text-sage-600 border border-sage-200">
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                        Compliance: Pass
                      </span>
                    )}
                  </div>
                </div>

                {selectedItem.content_html ? (
                  <div
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: selectedItem.content_html }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-charcoal-400 bg-cream-50 p-4 rounded-xl overflow-auto max-h-96">
                    {selectedItem.content}
                  </pre>
                )}

                {selectedItem.metadata && Object.keys(selectedItem.metadata).length > 0 && (
                  <div className="mt-6 pt-6 border-t border-charcoal-100">
                    <h3 className="font-medium text-charcoal-500 mb-3">Metadata</h3>
                    <div className="text-sm text-charcoal-400 space-y-2">
                      {Object.entries(selectedItem.metadata).map(([key, value]) => (
                        <div key={key} className="p-2 rounded-lg bg-cream-50">
                          <strong className="text-charcoal-500">{key}:</strong> {JSON.stringify(value)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm text-center py-16">
              <EyeIcon className="h-12 w-12 text-charcoal-200 mx-auto mb-4" />
              <p className="text-charcoal-300">Select a content item to preview</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
